import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaTimes,
  FaSave,
  FaTimesCircle,
  FaBarcode,
  FaMinus,
  FaPlus,
} from 'react-icons/fa';

import { useAuth } from '~/hooks';
import * as Yup from 'yup';

import api from '~/services/api';

import { Input, Select } from '~/components/Form';
import { TableLoading } from '~/components/Table';

import { Container, Header, Controls } from '~/styles/components';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import ConfirmWindow from '~/components/ConfirmWindow';
import { confirmAlert } from 'react-confirm-alert';
import formatValueOnChange from '~/util/formatValueOnChange';
import formatValue from '~/util/formatValue';
import { Content, BilletForm, Finances, Finance } from './styles';

const Form = () => {
  const { company } = useAuth();
  const formRef = useRef(null);
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [clientsOptions, setClientsOptions] = useState([]);
  const [eventsOptions, seteventsOptions] = useState([]);
  const [financialOptions, setFinancialOptions] = useState([]);

  const [finance, setFinance] = useState([]);

  const [monthlyPayment, setMonthlyPayment] = useState({
    company_id: company?.old_id || '',
    relationship_id: null,
    mainid: 1,
    financial: 'Asaas', // Valor padrão: Asaas
    species: 4, // Valor padrão: Duplicata de serviço
    emission_day: '01',
    expiration_type: 'Fixo',
    expiration_day: '5', // Valor padrão: 5
    expiration_month: 1,
    accept: false,
    price: '',
    employee_limit: company?.employee_limit || 0,
    discount: 0,
    drawee: '',
    drawer: null,
    interest_type: 2, // 1 = (R$) Valor por dia, 2 = (%) ao mês
    interest: 2, // Valor padrão: 2
    fine_type: 3, // 1 = Não, 2 = Valor fixo, 3 = Percentual
    fine: 2, // Valor padrão: 2
    days_to_fine: 1,
    protest: 2, // 1 = Sim, 2 = Não
    days_to_protest: 0,
    how_to_charge: 1, // 1 = Email, 2 = SMS, 3 = Correio
    message: '',
    active: true,
    status: false,
    allow_charge_again: true,
    allow_charge_again_sms: false,
  });

  // helper: converte valor para inteiro não-negativo
  const toNonNegativeInt = value => {
    const parsed = parseInt(value || '0', 10);
    if (Number.isNaN(parsed)) return 0;
    return Math.max(0, parsed);
  };

  const loadFinancialData = useCallback(async () => {
    if (company) {
      try {
        const response = await api.get(`/old-database/financial-data`, {
          params: {
            company_id: company.old_id,
          },
        });

        const options = response.data.map(item => ({
          value: item.id,
          label: item.title,
        }));

        setFinancialOptions(options);
      } catch (error) {
        toast.error(
          `Falha ao buscar dados financeiros: ${JSON.stringify(
            error.response?.data?.error
          ) || error.message}`,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      }
    }
  }, [company]);

  const loadClients = useCallback(async () => {
    if (company) {
      try {
        const response = await api.get(`/relationships`, {
          params: {
            company_id: company.id,
            selectOnly: true,
            active: true,
            type: 1,
          },
        });

        const options = response.data.map(item => ({
          value: item.old_id,
          label: item.name,
        }));

        setClientsOptions(options);
        setLoading(false);
      } catch (error) {
        toast.error(
          `Falha ao buscar clientes: ${JSON.stringify(
            error.response?.data?.error
          ) || error.message}`,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      }
    }
  }, [company]);

  const loadEvents = useCallback(async () => {
    if (company) {
      try {
        const response = await api.get('/old-database/events', {
          params: {
            company: company.id,
            monthly_payments: true,
            billet: true,
          },
        });

        if (response.data) {
          const options = response.data.map(event => ({
            value: event.id,
            label: event.name_label,
          }));
          seteventsOptions(options);
        }
      } catch (error) {
        toast.error(
          `Falha ao buscar eventos: ${JSON.stringify(
            error.response?.data?.error
          ) || error.message}`,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      }
    }
  }, [company]);

  const totalFinancePrice = useMemo(() => {
    return finance.reduce((total, item) => {
      const price = parseFloat(
        item.price?.replace(/[^\d,]/g, '').replace(',', '.')
      );
      const result = total + (isNaN(price) ? 0 : price);
      return result;
    }, 0);
  }, [finance]);

  useEffect(() => {
    setMonthlyPayment(prevMonthlyPayment => ({
      ...prevMonthlyPayment,
      price: formatValue(totalFinancePrice),
    }));
  }, [totalFinancePrice]);

  useEffect(() => {
    loadFinancialData();
    loadClients();
    loadEvents();
  }, [loadFinancialData, loadClients, loadEvents]);

  // Seleciona automaticamente Asaas quando os dados financeiros forem carregados
  useEffect(() => {
    if (financialOptions.length > 0) {
      const asaasOption = financialOptions.find(
        option => option.label.toLowerCase() === 'asaas'
      );

      if (asaasOption) {
        setMonthlyPayment(prevState => ({
          ...prevState,
          financial: asaasOption.value,
        }));
      }
    }
  }, [financialOptions]);

  const monthlyPaymentSchema = Yup.object().shape({
    drawee: Yup.string().required('Cliente é obrigatório'),
    price: Yup.string().required('Preço é obrigatório'),
    species: Yup.string().required('Espécie é obrigatória'),
    financial: Yup.string().required('Dados financeiros é obrigatório'),
    emission_day: Yup.string().required('Dia de emissão é obrigatório'),
    expiration_day: Yup.string().required('Dia de vencimento é obrigatório'),
    interest: Yup.number().required('Juros é obrigatório'),
    interest_type: Yup.number().required('Tipo de juros é obrigatório'),
    fine_type: Yup.number().required('Tipo de multa é obrigatória'),
    fine: Yup.mixed().when('fine_type', {
      is: value => value === 1, // 1 = Não
      then: Yup.mixed(),
      otherwise: Yup.mixed().required('Multa é obrigatória'),
    }),
    days_to_fine: Yup.mixed().when('fine_type', {
      is: value => value === 1, // 1 = Não
      then: Yup.mixed().nullable(),
      otherwise: Yup.mixed().required('Dias para multar é obrigatório'),
    }),
    protest: Yup.number().required('Protesto é obrigatório'),
    message: Yup.string().nullable(),
    how_to_charge: Yup.number().required('Forma de cobrança é obrigatória'),
  });

  const handleSave = useCallback(async () => {
    try {
      const company_id = company.old_id;

      const monthly_payment_finance = finance.map(item => ({
        ...item,
        company_id,
        competence: null, // Garantindo que competence será sempre null
        price: item.price?.replace(/[^\d,]/g, '').replace(',', '.'),
        temp_id: null,
      }));

      if (monthly_payment_finance.length === 0) {
        toast.error(
          'É obrigatório informar pelo menos um registro financeiro',
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
        return;
      }

      const formattedMonthlyPayment = {
        ...monthlyPayment,
        company_id,
        monthly_payment_finance,
      };

      await monthlyPaymentSchema.validate(formattedMonthlyPayment, {
        abortEarly: false,
      });

      await api.post('/monthly-payments', formattedMonthlyPayment);

      toast.success('Mensalidade criada com sucesso.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      // voltar para a rota anterior se possível; caso contrário, fallback para a listagem
      if (history.length > 1 && history.action !== 'REPLACE') {
        history.goBack();
      } else {
        history.push('/monthly-payments');
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const validationErrors = {};
        error.inner.forEach(err => {
          validationErrors[err.path] = err.message;
          toast.error(err.message, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        });
        formRef.current.setErrors(validationErrors);
      } else {
        toast.error(
          `Falha ao salvar mensalidade: ${JSON.stringify(
            error.response?.data?.error
          ) || error.message}`,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      }
    }
  }, [monthlyPayment, monthlyPaymentSchema, finance, history, company]);

  const confirmSaveForm = useCallback(() => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmWindow
            onClick={handleSave}
            onClose={onClose}
            message="Todos os dados serão salvos com esta ação."
          />
        );
      },
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }, [handleSave]);

  const handleCancel = useCallback(() => {
    if (history.length > 1 && history.action !== 'REPLACE') {
      history.goBack();
    } else {
      history.push('/monthly-payments');
    }
  }, [history]);

  const confirmCalcelForm = useCallback(() => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return <ConfirmWindow onClick={handleCancel} onClose={onClose} />;
      },
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }, [handleCancel]);

  const handleChange = (e, name) => {
    const { value } = e.target;

    setMonthlyPayment(prevMonthlyPayment => ({
      ...prevMonthlyPayment,
      [name]: value,
    }));
  };

  const handleChangeFinance = (e, index, name) => {
    const { value } = e.target;

    setFinance(prevFinance =>
      prevFinance.map(item => {
        if (item.temp_id !== index) return item;

        const updatedItem = { ...item };

        if (name === 'price') {
          const formattedValue = formatValueOnChange(value);
          updatedItem[name] = formattedValue;
        } else {
          updatedItem[name] = value;
        }

        return updatedItem;
      })
    );
  };

  const handleChangeSelect = (selectedOption, name) => {
    setMonthlyPayment(prevMonthlyPayment => {
      const newMonthlyPayment = {
        ...prevMonthlyPayment,
        [name]: selectedOption.value,
      };

      if (name === 'protest' && selectedOption.value === 2) {
        // Se o protesto for "Não" (valor 2), dias para protesto é null
        newMonthlyPayment.days_to_protest = null;
      }

      if (
        newMonthlyPayment.financial !== '' &&
        newMonthlyPayment.protest !== 2 &&
        newMonthlyPayment.drawee !== ''
      ) {
        // loadDaysToProtest(
        //   newMonthlyPayment.financial,
        //   newMonthlyPayment.protest
        // );
      }
      return newMonthlyPayment;
    });
  };

  const handleChangeSelectFinance = (selectedOption, index, name) => {
    setFinance(prevFinance =>
      prevFinance.map(item => {
        if (item.temp_id !== index) return item;

        const updatedItem = { ...item, [name]: selectedOption.value };
        return updatedItem;
      })
    );
  };

  const expirationTypeOptions = [
    { value: 'Fixo', label: 'Fixo' },
    { value: 'Variável', label: 'Variável' },
  ];

  const speciesOptions = [
    { value: 4, label: 'Duplicata de serviço' },
    { value: 2, label: 'Fatura' },
  ];

  const interestTypeOptions = [
    { value: 1, label: '(R$) Valor por dia' },
    { value: 2, label: '(%) ao mês' },
  ];

  const fineTypeOptions = [
    { value: 1, label: 'Não' },
    { value: 2, label: 'Valor fixo' },
    { value: 3, label: 'Percentual' },
  ];

  const protestOptions = [
    { value: 1, label: 'Sim' },
    { value: 2, label: 'Não' },
  ];

  const howToChargeOptions = [
    { value: 1, label: 'Email' },
    { value: 2, label: 'SMS' },
    { value: 3, label: 'Correio' },
  ];

  const handleAddNewFinance = useCallback(() => {
    const newFinance = {
      company_id: monthlyPayment.company_id,
      mainid: 1,
      date: new Date(),
      account: '',
      financial: monthlyPayment.financial,
      name: '',
      competence: null, // Definindo competence como null por padrão
      price: '',
      status: monthlyPayment.status,
      temp_id: Date.now(), // Adicionando um temp_id único baseado no timestamp
    };

    setFinance(prevFinance => [newFinance, ...(prevFinance || [])]);
  }, [monthlyPayment]);

  const handleRemoveFinance = useCallback(async index => {
    setFinance(prevFinance =>
      prevFinance
        .map(item =>
          item.temp_id === index ? { ...item, status: true } : item
        )
        .filter(item => item.temp_id !== index)
    );
  }, []);

  return (
    <Container>
      <Header>
        <div>
          <FaBarcode />
          <h1>Cobrança - Mensalidades</h1>
        </div>

        <Link to="/">
          <FaTimes />
        </Link>
      </Header>
      <Controls>
        <button type="button" onClick={confirmSaveForm}>
          <FaSave size={15} color="#44546a" />
          <span>Salvar</span>
        </button>
        <button type="button" onClick={confirmCalcelForm}>
          <FaTimesCircle />
          <span>Cancelar</span>
        </button>
      </Controls>
      {loading ? (
        <TableLoading />
      ) : (
        <Content className="content">
          <BilletForm ref={formRef} onSubmit={handleSave}>
            <h4>Mensalidade</h4>
            <section>
              <Select
                label="Cliente (Sacado)"
                name="drawee"
                className="drawee"
                options={clientsOptions}
                placeholder="Selecione um cliente"
                value={clientsOptions.find(
                  option => option.value === monthlyPayment?.drawee
                )}
                onChange={option => handleChangeSelect(option, 'drawee')}
              />
              <Input
                name="price"
                className="price"
                label="Preço"
                value={monthlyPayment.price}
                readOnly
              />
              <Select
                label="Dados financeiros"
                name="financial"
                className="financial_data"
                options={financialOptions}
                placeholder="Selecione um dado financeiro"
                value={financialOptions.find(
                  option => option.value === monthlyPayment?.financial
                )}
                onChange={option => handleChangeSelect(option, 'financial')}
              />
              <Input
                name="employee_limit"
                className="employee_limit"
                label="Limite de empregados"
                type="number"
                min={0}
                step={1}
                value={monthlyPayment.employee_limit}
                onChange={e => {
                  setMonthlyPayment(prev => ({
                    ...prev,
                    employee_limit: toNonNegativeInt(e.target.value),
                  }));
                }}
              />
            </section>
            <section>
              <Input
                name="emission_day"
                className="emission_day"
                label="Dia de Emissão"
                value={monthlyPayment.emission_day}
                onChange={e => handleChange(e, 'emission_day')}
              />
              <Select
                label="Tipo de vencimento"
                name="expiration_type"
                className="expiration_type"
                options={expirationTypeOptions}
                placeholder="Selecione um tipo de vencimento"
                value={expirationTypeOptions.find(
                  option => option.value === monthlyPayment?.expiration_type
                )}
                onChange={option =>
                  handleChangeSelect(option, 'expiration_type')
                }
              />
              <Input
                name="expiration_day"
                className="expiration_day"
                label="Dia de Vencimento"
                value={monthlyPayment.expiration_day}
                onChange={e => handleChange(e, 'expiration_day')}
              />
              <Select
                label="Espécie"
                name="species"
                className="species"
                options={speciesOptions}
                placeholder="Selecione a espécie"
                value={speciesOptions.find(
                  option => option.value === monthlyPayment?.species
                )}
                onChange={option => handleChangeSelect(option, 'species')}
                isDisabled
              />
            </section>
            <section>
              <Input
                name="interest"
                className="interest"
                label="Juros"
                value={monthlyPayment.interest}
                onChange={e => handleChange(e, 'interest')}
              />
              <Select
                label="Juros (Tipo)"
                name="interest_type"
                className="interest_type"
                options={interestTypeOptions}
                placeholder="Selecione a forma de juros"
                value={interestTypeOptions.find(
                  option => option.value === monthlyPayment?.interest_type
                )}
                onChange={option => handleChangeSelect(option, 'interest_type')}
              />
              <Input
                name="fine"
                className="fine"
                label="Multa"
                value={monthlyPayment.fine}
                onChange={e => handleChange(e, 'fine')}
              />
              <Select
                label="Multa (Tipo)"
                name="fine_type"
                className="fine_type"
                options={fineTypeOptions}
                placeholder="Selecione a forma de multa"
                value={fineTypeOptions.find(
                  option => option.value === monthlyPayment?.fine_type
                )}
                onChange={option => handleChangeSelect(option, 'fine_type')}
              />
              <Input
                name="days_to_fine"
                className="days_to_fine"
                label="Dias para multar"
                value={monthlyPayment.days_to_fine}
                onChange={e => handleChange(e, 'days_to_fine')}
              />
              <Select
                label="Protestar"
                name="protest"
                className="protest"
                options={protestOptions}
                placeholder="Selecione a forma de protesto"
                value={protestOptions.find(
                  option => option.value === monthlyPayment?.protest
                )}
                onChange={option => handleChangeSelect(option, 'protest')}
              />
              <Select
                label="Forma de Cobrança"
                name="how_to_charge"
                className="how_to_charge"
                options={howToChargeOptions}
                placeholder="Selecione a forma de cobrança"
                value={howToChargeOptions.find(
                  option => option.value === monthlyPayment?.how_to_charge
                )}
                onChange={option => handleChangeSelect(option, 'how_to_charge')}
              />
            </section>
            <section>
              <Input
                name="message"
                className="message"
                label="Mensagem"
                value={monthlyPayment?.message}
                onChange={e => handleChange(e, 'message')}
              />
            </section>

            <Finances>
              <header>
                <h4>Registros Financeiros</h4>
                <button type="button" onClick={handleAddNewFinance}>
                  <FaPlus size={10} />
                </button>
              </header>

              {finance?.length > 0 &&
                finance.map(item => (
                  <Finance key={item.temp_id}>
                    <button
                      type="button"
                      onClick={() => handleRemoveFinance(item.temp_id)}
                    >
                      <FaMinus size={10} />
                    </button>

                    <section>
                      <Select
                        label="Evento"
                        name="event"
                        className="account"
                        options={eventsOptions}
                        placeholder="Selecione um evento"
                        value={eventsOptions.find(
                          option => option.value === item.event
                        )}
                        onChange={option =>
                          handleChangeSelectFinance(
                            option,
                            item.temp_id,
                            'account'
                          )
                        }
                      />
                      {/* Campo de competência removido, será enviado como null */}
                      <Input
                        name="price"
                        className="price"
                        label="Valor"
                        value={item.price}
                        onChange={e =>
                          handleChangeFinance(e, item.temp_id, 'price')
                        }
                      />
                    </section>
                  </Finance>
                ))}
            </Finances>
          </BilletForm>
        </Content>
      )}
    </Container>
  );
};

export default Form;
